import { Alert, Box, MenuItem, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import {
  FeedbackSnackbar,
  FormDialog,
  FormTextField,
  ResourcePage,
  ResourceCollection,
  StatusBadge,
  ValueBadge,
  type ResourceCollectionState,
} from '../../../design-system';
import {
  CommonCatalogFields,
  commonDraftValid,
  emptyCommonDraft,
  type CommonCatalogDraft,
} from './ReferenceCatalogFields';
import { BackToReferenceData, canEditCatalogItem, catalogActionsColumn, CatalogDetailDialog, useReferenceLifecycle } from './ReferenceLifecycle';
import { ReferenceBulkCreateDialog } from './ReferenceBulkCreateDialog';
import { referenceCreateActions } from './ReferenceCreateActions';
import {
  apiErrorMessage,
  filterCatalog,
  fixtureReferenceCode,
  replaceCatalogItem,
  replaceCatalogItems,
  sortCatalog,
  statusLabel,
  statusOptions,
  statusTone,
  todayInSeoul,
  useOperationKey,
  workerTypeLabel,
  workerTypeOptions,
  type ReferenceDataResource,
  type ReferenceSettingsCommonProps,
} from '../model/referenceData';
import { referenceBulkPayloads, referenceBulkWorkflows } from '../model/referenceBulk';
import type { ReferenceJobType, ReferenceJobTypeChangeInput, ReferenceJobTypeCreateInput } from '../model/types';
type JobTypeDraft = CommonCatalogDraft & {
  workerType: ReferenceJobType['workerType'] | '';
};
type JobTypeEditor = {
  mode: 'create' | 'edit';
  item?: ReferenceJobType;
  draft: JobTypeDraft;
};
function jobTypeDraftValid(draft: JobTypeDraft, mode: JobTypeEditor['mode']) {
  return commonDraftValid(draft, mode) && Boolean(draft.workerType);
}
export function JobTypeReferenceSettings({
  port,
  fixtureMode,
  canRead,
  canWrite,
  onBack,
  titleRef,
  resource,
}: ReferenceSettingsCommonProps & {
  resource: ReferenceDataResource<ReferenceJobType>;
}) {
  const [items, setItems] = useState<ReferenceJobType[]>(resource.data ?? []);
  const [selected, setSelected] = useState<ReferenceJobType>();
  const [editor, setEditor] = useState<JobTypeEditor>();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [workerType, setWorkerType] = useState('');
  const [busy, setBusy] = useState(false);
  const [editorError, setEditorError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [bulkWarning, setBulkWarning] = useState<string>();
  const [bulkOpen, setBulkOpen] = useState(false);
  const operationKey = useOperationKey();
  const bulkWorkflow = useMemo(() => referenceBulkWorkflows.jobTypes(), []);
  useEffect(() => setItems(sortCatalog(resource.data ?? [])), [resource.data]);
  const filteredItems = useMemo(
    () => filterCatalog(items, query, status).filter((item) => !workerType || item.workerType === workerType),
    [items, query, status, workerType],
  );
  const columns = useMemo<GridColDef<ReferenceJobType>[]>(
    () => [
      {
        field: 'descriptor',
        renderCell: ({ row }) => <ValueBadge label={row.descriptor} />,
        headerName: '고용·외부 인력 유형',
        flex: 1,
        minWidth: 190,
      },
      {
        field: 'code',
        headerName: '참조 코드',
        width: 170,
      },
      {
        field: 'workerType',
        headerName: '적용 대상',
        width: 130,
        valueFormatter: (value: ReferenceJobType['workerType']) => workerTypeLabel(value),
        renderCell: ({ row }) => <ValueBadge label={workerTypeLabel(row.workerType)} />,
      },
      {
        field: 'status',
        headerName: '상태',
        width: 140,
        renderCell: ({ row }) => <StatusBadge label={statusLabel(row.status)} tone={statusTone(row.status)} />,
      },
    ],
    [],
  );
  const updateItem = (item: ReferenceJobType) => {
    setItems((current) => replaceCatalogItem(current, item));
    setSelected((current) => (current?.id === item.id ? item : current));
    if (!fixtureMode) resource.reload();
  };
  const lifecycle = useReferenceLifecycle<ReferenceJobType>({
    kindLabel: '고용·외부 인력 유형',
    fixtureMode,
    change: (id, input, key) => port.changeReferenceJobType(id, input as ReferenceJobTypeChangeInput, key),
    onChanged: updateItem,
    onNotice: setNotice,
  });
  const openCreate = () => {
    setEditor({
      mode: 'create',
      draft: {
        ...emptyCommonDraft(),
        workerType: '',
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const openEdit = (item: ReferenceJobType) => {
    setSelected(undefined);
    setEditor({
      mode: 'edit',
      item,
      draft: {
        descriptor: item.descriptor,
        workerType: item.workerType,
        effectiveDate: item.status === 'SCHEDULED' ? item.effectiveFrom : todayInSeoul(),
        reason: '',
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const changeDraft = <K extends keyof JobTypeDraft>(field: K, value: JobTypeDraft[K]) => {
    setEditor((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              [field]: value,
            },
          }
        : current,
    );
    setEditorError(undefined);
  };
  const save = () => {
    if (!editor || !jobTypeDraftValid(editor.draft, editor.mode) || busy) return;
    setBusy(true);
    setEditorError(undefined);
    let request: Promise<ReferenceJobType>;
    if (editor.mode === 'create') {
      const payload: ReferenceJobTypeCreateInput = {
        descriptor: editor.draft.descriptor.trim(),
        workerType: editor.draft.workerType as ReferenceJobType['workerType'],
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            id: `job-type-fixture-${crypto.randomUUID()}`,
            code: fixtureReferenceCode(),
            descriptor: payload.descriptor,
            workerType: payload.workerType,
            status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' : 'ACTIVE',
            effectiveFrom: payload.effectiveDate,
            version: 1,
          })
        : port.createReferenceJobType(payload, key);
    } else {
      const payload: ReferenceJobTypeChangeInput = {
        version: editor.item!.version,
        descriptor: editor.draft.descriptor.trim(),
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            ...editor.item!,
            descriptor: payload.descriptor ?? editor.item!.descriptor,
            effectiveFrom: payload.effectiveDate,
            version: editor.item!.version + 1,
          })
        : port.changeReferenceJobType(editor.item!.id, payload, key);
    }
    const savedMode = editor.mode;
    void request
      .then(
        (saved) => {
          updateItem(saved);
          setEditor(undefined);
          setNotice(`${saved.descriptor} 유형을 ${savedMode === 'create' ? '등록' : '변경'}했습니다.`);
          operationKey.reset();
        },
        (cause: unknown) => setEditorError(apiErrorMessage(cause, '고용·외부 인력 유형을 저장하지 못했습니다.')),
      )
      .finally(() => setBusy(false));
  };
  const collectionState: ResourceCollectionState = resource.loading
    ? {
        kind: 'loading',
        label: '고용·외부 인력 유형 목록을 불러오는 중입니다.',
      }
    : resource.error
      ? {
          kind: 'error',
          message: resource.error,
          onRetry: resource.reload,
          retryLabel: '유형 목록 다시 시도',
        }
      : items.length === 0
        ? {
            kind: 'empty',
            title: '등록된 고용·외부 인력 유형이 없습니다.',
            description: canWrite ? '유형 등록을 눌러 첫 항목을 추가해 주세요.' : '등록된 유형이 없습니다.',
          }
        : {
            kind: 'ready',
          };
  return (
    <>
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
      <ResourcePage
        title="고용·외부 인력 유형"
        titleRef={titleRef}
        width="standard"
        navigation={<BackToReferenceData onBack={onBack} />}
        {...(canWrite ? referenceCreateActions({
          createLabel: "유형 등록",
          onCreate: openCreate,
          onBulkCreate: () => setBulkOpen(true),
        }) : {})}
      >
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {bulkWarning && <Alert severity="warning" onClose={() => setBulkWarning(undefined)} sx={{ mb: 1.5, flexShrink: 0 }}>{bulkWarning}</Alert>}
          {!canRead ? (
            <Stack
              spacing={1.5}
              sx={{
                mb: canRead ? 1.5 : 0,
                flexShrink: 0,
              }}
            >
              {!canRead ? (
                <Alert severity="warning">
                  고용·외부 인력 유형 기준정보를 조회하려면 staffing.read 권한이 필요합니다.
                </Alert>
              ) : null}
            </Stack>
          ) : null}
          {canRead ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <ResourceCollection
                state={collectionState}
                rows={filteredItems}
                totalCount={items.length}
                columns={[...columns, catalogActionsColumn({ canWrite, onInspect: setSelected, onEdit: openEdit, onLifecycle: lifecycle.open })]}
                query={query}
                onQueryChange={setQuery}
                searchPlaceholder="유형 이름 또는 코드 검색"
                rowDisplayMode="all"
                showTotalCount={false}
                filters={[
                  {
                    label: '적용 대상',
                    value: workerType,
                    onChange: setWorkerType,
                    options: [
                      {
                        label: '전체',
                        value: '',
                      },
                      ...workerTypeOptions,
                    ],
                  },
                  {
                    label: '상태',
                    value: status,
                    onChange: setStatus,
                    options: statusOptions,
                  },
                ]}
                onRowClick={({ row }) => canWrite && canEditCatalogItem(row) ? openEdit(row) : setSelected(row)}
              />
            </Box>
          ) : null}
        </Box>
      </ResourcePage>

      <CatalogDetailDialog
        item={selected}
        kindLabel="고용·외부 인력 유형"
        canWrite={canWrite}
        canEdit={selected?.status !== 'SCHEDULED'}
        editDisabledReason={
          selected?.status === 'SCHEDULED' ? '사용 예정인 유형은 적용일 이후 수정할 수 있습니다.' : undefined
        }
        extraItems={selected ? [['적용 대상', <ValueBadge label={workerTypeLabel(selected.workerType)} />]] : []}
        onClose={() => setSelected(undefined)}
        onEdit={() => selected && openEdit(selected)}
        onLifecycle={(action) => selected && lifecycle.open(action, selected)}
      />

      <FormDialog
        open={Boolean(editor)}
        onClose={() => {
          if (!busy) setEditor(undefined);
        }}
        onSubmit={save}
        title={editor?.mode === 'create' ? '고용·외부 인력 유형 등록' : '고용·외부 인력 유형 수정'}
        cancelLabel="취소"
        submitLabel="변경사항 저장"
        busy={busy}
        busyLabel="저장 중…"
        submitDisabled={!editor || !jobTypeDraftValid(editor.draft, editor.mode)}
        maxWidth="sm"
      >
        {editor ? (
          <Stack spacing={2}>
            {editorError ? (
              <Alert severity="error" role="alert">
                {editorError}
              </Alert>
            ) : null}
            <CommonCatalogFields
              draft={editor.draft}
              mode={editor.mode}
              busy={busy}
              descriptorHelper="임직원의 고용 형태나 외부 인력 유형을 구분할 이름입니다."
              onChange={(field, value) => changeDraft(field, value)}
            />
            <FormTextField
              select
              required
              label="적용 대상"
              value={editor.draft.workerType}
              disabled={busy || editor.mode === 'edit'}
              error={!editor.draft.workerType}
              helperText={
                editor.mode === 'edit'
                  ? '적용 대상은 등록 후 변경할 수 없습니다.'
                  : undefined
              }
              onChange={(event) => changeDraft('workerType', event.target.value as ReferenceJobType['workerType'])}
            >
              {workerTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ValueBadge label={option.label} />
                </MenuItem>
              ))}
            </FormTextField>
          </Stack>
        ) : null}
      </FormDialog>
      <ReferenceBulkCreateDialog<ReferenceJobType>
        open={bulkOpen}
        workflow={bulkWorkflow}
        onClose={() => setBulkOpen(false)}
        create={(row, key) => {
          const payload = referenceBulkPayloads.jobType(row);
          return fixtureMode
            ? Promise.resolve({
                id: `job-type-fixture-${crypto.randomUUID()}`,
                code: fixtureReferenceCode(),
                descriptor: payload.descriptor,
                workerType: payload.workerType,
                status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' as const : 'ACTIVE' as const,
                effectiveFrom: payload.effectiveDate,
                version: 1,
              })
            : port.createReferenceJobType(payload, key);
        }}
        onCreated={(created) => {
          setItems((current) => replaceCatalogItems(current, created));
          if (!fixtureMode) resource.reload();
        }}
        onApplied={({ succeeded, failed }) => {
          setBulkWarning(failed ? `고용·외부 인력 유형 ${succeeded.toLocaleString()}개를 등록했고 ${failed.toLocaleString()}개는 실패했습니다.` : undefined);
          setNotice(failed ? undefined : `고용·외부 인력 유형 ${succeeded.toLocaleString()}개를 등록했습니다.`);
        }}
      />
      {lifecycle.dialog}
    </>
  );
}
